import { useState, useRef, useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import SbUtils from '../../Utils/SbUtils';

/**
 * VisibilitySelector Component
 *
 * Reusable component for selecting pages, categories, or custom post types
 * in the Visibility section. Uses simple ID arrays for clean data structure.
 *
 * @param {Object} props
 * @param {string} props.label - Display text ("Pages", "Categories", etc.)
 * @param {string} props.type - Type identifier ('page', 'category', 'custom_post_type')
 * @param {string} props.icon - Icon name for list items ('document', 'folder')
 * @param {Array} props.items - Available items to select from
 * @param {Array} props.selectedIds - Array of selected IDs/slugs
 * @param {Function} props.onSelect - Called when item is selected: (item) => void
 * @param {Function} props.onRemove - Called when item is removed: (id) => void
 * @param {string} props.searchPlaceholder - Placeholder text for search input
 * @param {Object} props.itemConfig - Adapters for item shape: { getKey, getLabel, getUrl }
 * @param {boolean} props.disabled - Optional - disable when no items available
 */
const VisibilitySelector = ( {
    label,
    type,
    icon = 'document',
    items = [],
    selectedIds = [],
    onSelect,
    onRemove,
    searchPlaceholder = '',
    itemConfig = {},
    disabled = false
} ) => {
    // Internal state for expand/search
    const [ isExpanded, setIsExpanded ] = useState( false );
    const [ searchQuery, setSearchQuery ] = useState( '' );

    // Ref for click outside detection
    const containerRef = useRef( null );

    // Close dropdown when clicking outside
    useEffect( () => {
        const handleClickOutside = ( event ) => {
            if ( containerRef.current && !containerRef.current.contains( event.target ) ) {
                setIsExpanded( false );
            }
        };

        if ( isExpanded ) {
            document.addEventListener( 'mousedown', handleClickOutside );
        }

        return () => {
            document.removeEventListener( 'mousedown', handleClickOutside );
        };
    }, [ isExpanded ] );

    // Default item config adapters
    const getKey = itemConfig.getKey || ( ( item ) => item.id );
    const getLabel = itemConfig.getLabel || ( ( item ) => item.title || item.name || item.label );
    const getUrl = itemConfig.getUrl || ( ( item ) => item.url || '' );

    // Get selected items by matching IDs with available items
    const selectedItems = items.filter( ( item ) => selectedIds.includes( getKey( item ) ) );
    const hasSelectedItems = selectedItems.length > 0;

    // Check if an item is selected
    const isItemSelected = ( item ) => {
        return selectedIds.includes( getKey( item ) );
    };

    // Filter items by search query
    const filteredItems = items.filter( ( item ) =>
        getLabel( item ).toLowerCase().includes( searchQuery.toLowerCase() )
    );

    // Handle toggle dropdown
    const handleToggleDropdown = () => {
        if ( !disabled ) {
            setIsExpanded( !isExpanded );
        }
    };

    // Handle item selection
    const handleSelectItem = ( item ) => {
        if ( onSelect ) {
            onSelect( item );
        }
        // Close dropdown after selection
        setIsExpanded( false );
    };

    // Handle item removal
    const handleRemoveItem = ( item ) => {
        if ( onRemove ) {
            onRemove( getKey( item ) );
        }
    };

    // Handle search input change
    const handleSearchChange = ( e ) => {
        setSearchQuery( e.target.value );
    };

    return (
        <div ref={ containerRef } className={ `sb-popup-page-dropdown-ctn sb-fs ${ hasSelectedItems ? 'sb-has-items' : '' }` }>
            { hasSelectedItems ? (
                <>
                    {/* Header with title, count, and add button */}
                    <div className="sb-popup-selected-header">
                        <div className="sb-popup-selected-header-left">
                            <span className="sb-popup-selected-header-title">{ label }</span>
                            <span className="sb-popup-selected-count">{ selectedItems.length }</span>
                        </div>
                        <button
                            type="button"
                            className="sb-popup-add-btn"
                            onClick={ handleToggleDropdown }
                        >
                            { SbUtils.printIcon( 'plus', 'sb-plus-icon' ) }
                        </button>
                    </div>
                    {/* Selected Items List */}
                    <div className="sb-popup-selected-items">
                        { selectedItems.map( ( item, index ) => (
                            <div key={ `${type}-${getKey( item )}-${index}` } className="sb-popup-page-item sb-popup-page-item--selected">
                                { SbUtils.printIcon( icon, 'sb-popup-page-item-icon', false, 20 ) }
                                <div className="sb-popup-page-item-content">
                                    <div className="sb-popup-page-item-title">{ getLabel( item ) }</div>
                                    { getUrl( item ) && <div className="sb-popup-page-item-url">{ getUrl( item ) }</div> }
                                </div>
                                <button
                                    type="button"
                                    className="sb-popup-delete-btn"
                                    onClick={ ( e ) => {
                                        e.stopPropagation();
                                        handleRemoveItem( item );
                                    } }
                                >
                                    { SbUtils.printIcon( 'trash', '', false, 18 ) }
                                </button>
                            </div>
                        ) ) }
                    </div>
                </>
            ) : (
                disabled ? (
                    <div className="sb-popup-add-page-btn sb-popup-add-page-btn--disabled sb-fs">
                        { SbUtils.printIcon( 'info', 'sb-info-icon', false, 16 ) }
                        <span>{ __( 'No', 'reviews-feed' ) } { label.toLowerCase() } { __( 'available', 'reviews-feed' ) }</span>
                    </div>
                ) : (
                    <button
                        type="button"
                        className={ `sb-popup-add-page-btn sb-popup-add-page-btn--solid sb-fs ${ isExpanded ? 'sb-active' : '' }` }
                        onClick={ handleToggleDropdown }
                    >
                        { SbUtils.printIcon( 'plus', 'sb-plus-icon' ) }
                        <span>{ label }</span>
                        { SbUtils.printIcon( isExpanded ? 'chevron-top' : 'chevron-bottom', 'sb-popup-btn-chevron', false, 10 ) }
                    </button>
                )
            ) }

            {/* Dropdown */}
            { isExpanded && (
                <div className="sb-popup-page-dropdown">
                    <div className="sb-popup-dropdown-search">
                        <input
                            type="text"
                            placeholder={ searchPlaceholder }
                            value={ searchQuery }
                            onChange={ handleSearchChange }
                        />
                    </div>
                    <div className="sb-popup-dropdown-items">
                        { filteredItems.length > 0 ? (
                            filteredItems.map( ( item ) => (
                                <div
                                    key={ getKey( item ) }
                                    className="sb-popup-page-item"
                                    data-active={ isItemSelected( item ) }
                                    onClick={ () => handleSelectItem( item ) }
                                >
                                    { SbUtils.printIcon( icon, 'sb-popup-page-item-icon', false, 20 ) }
                                    <div className="sb-popup-page-item-content">
                                        <div className="sb-popup-page-item-title">{ getLabel( item ) }</div>
                                        { getUrl( item ) && <div className="sb-popup-page-item-url">{ getUrl( item ) }</div> }
                                    </div>
                                </div>
                            ) )
                        ) : (
                            <div className="sb-popup-dropdown-empty">
                                <span>
                                    { searchQuery
                                        ? __( 'No matching items', 'reviews-feed' )
                                        : __( 'No items found', 'reviews-feed' )
                                    }
                                </span>
                            </div>
                        ) }
                    </div>
                </div>
            ) }
        </div>
    );
};

export default VisibilitySelector;
